"use client";

import { useMemo } from "react";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { Skeleton } from "@/components/ui/skeleton";

// Definisikan tipe data sesuai input Anda
interface RekapItem {
  prodi_nama: string;
  jumlah_pendaftar: number;
  jumlah_lulus: number;
  jumlah_bayar_ukt: number;
}

interface DataRekap {
  rekap: RekapItem[];
  total: any;
}

interface AdmissionStatusChartProps {
  data: DataRekap | undefined;
  isLoading: boolean;
}

// Konfigurasi warna dan label chart
const chartConfig = {
  pendaftar: {
    label: "Pendaftar",
    color: "hsl(var(--chart-1))", // Biasanya biru/primary
  },
  lulus: {
    label: "Lulus Seleksi",
    color: "hsl(var(--chart-2))", // Biasanya hijau/teal
  },
  bayar: {
    label: "Bayar UKT",
    color: "hsl(var(--chart-3))", // Biasanya orange/amber
  },
} satisfies ChartConfig;

export function AdmissionStatusChart({
  data,
  isLoading,
}: AdmissionStatusChartProps) {
  // Proses data: Urutkan berdasarkan pendaftar terbanyak dan ambil Top 10
  // agar chart tidak terlalu panjang ke bawah
  const chartData = useMemo(() => {
    if (!data?.rekap) return [];

    return [...data.rekap]
      .sort((a, b) => b.jumlah_pendaftar - a.jumlah_pendaftar)
      .slice(0, 10)
      .map((item) => ({
        prodi: item.prodi_nama,
        pendaftar: item.jumlah_pendaftar,
        lulus: item.jumlah_lulus,
        bayar: item.jumlah_bayar_ukt,
      }));
  }, [data]);

  if (isLoading) {
    return (
      <Card className="flex flex-col h-full min-h-[400px]">
        <CardHeader>
          <Skeleton className="h-6 w-1/2 mb-2" />
          <Skeleton className="h-4 w-1/3" />
        </CardHeader>
        <CardContent className="flex-1">
          <Skeleton className="h-full w-full rounded-xl" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="flex flex-col h-full">
      <CardHeader>
        <CardTitle>Status Penerimaan per Prodi (Top 10)</CardTitle>
        <CardDescription>
          Perbandingan jumlah pendaftar, kelulusan, dan pembayaran UKT pada
          program studi terpopuler.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-1 pb-4">
        <ChartContainer config={chartConfig} className="min-h-[350px] w-full">
          <BarChart
            accessibilityLayer
            data={chartData}
            layout="vertical"
            margin={{
              left: 20, // Margin kiri untuk label prodi yang panjang
              right: 20,
            }}
          >
            <CartesianGrid horizontal={false} strokeDasharray="3 3" />

            {/* YAxis untuk Nama Prodi (Text truncate handled by Recharts mostly, but layout helps) */}
            <YAxis
              dataKey="prodi"
              type="category"
              tickLine={false}
              tickMargin={10}
              axisLine={false}
              width={150} // Lebar area label prodi
              style={{ fontSize: "11px", fontWeight: 500 }}
              tickFormatter={(value) => {
                // Potong teks jika terlalu panjang (opsional)
                return value.length > 25
                  ? `${value.substring(0, 25)}...`
                  : value;
              }}
            />

            <XAxis type="number" hide />

            <ChartTooltip
              cursor={false}
              content={<ChartTooltipContent indicator="dashed" />}
            />

            <ChartLegend content={<ChartLegendContent />} />

            {/* Bar 1: Pendaftar */}
            <Bar
              dataKey="pendaftar"
              fill="var(--color-pendaftar)"
              radius={[0, 4, 4, 0]}
              barSize={12} // Ukuran bar lebih tipis agar muat
            />

            {/* Bar 2: Lulus */}
            <Bar
              dataKey="lulus"
              fill="var(--color-lulus)"
              radius={[0, 4, 4, 0]}
              barSize={12}
            />

            {/* Bar 3: Bayar */}
            <Bar
              dataKey="bayar"
              fill="var(--color-bayar)"
              radius={[0, 4, 4, 0]}
              barSize={12}
            />
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
